import { supabase } from '../lib/supabase'
import {
  applyFilters,
  applyOrdering,
  applyRange,
  applyRoleConstraints,
  applySearch,
  applyOwnershipToPayload,
  callRpc,
  handleQuery,
  accessDeniedResponse,
  resolveRoleContext,
  ensureCanManageEvent
} from './utils'

const TABLE_NAME = 'payment_history'

/**
 * Fetch payments with optional filtering and role constraints.
 * @param {{
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string,
 *   eventIds?: string[],
 *   filters?: Array<{ column: string, operator: string, value: any }>,
 *   search?: string,
 *   searchColumns?: string[],
 *   orderBy?: { column: string, ascending?: boolean },
 *   range?: { from: number, to: number }
 * }} [options]
 */
export const listPayments = async (options = {}) => {
  const {
    role,
    userId,
    eventIds,
    filters,
    search,
    searchColumns = ['reference', 'email'],
    orderBy = { column: 'created_at', ascending: false },
    range
  } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && (!eventIds || eventIds.length === 0)) {
    return accessDeniedResponse('Partners can only access payments for assigned events.')
  }

  let query = supabase.from(TABLE_NAME).select('*')

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'agent_id',
    studentField: 'payer_id',
    eventIds
  })

  query = applyFilters(query, filters)
  query = applySearch(query, search, searchColumns)
  query = applyOrdering(query, orderBy)
  query = applyRange(query, range)

  return handleQuery(query, { fallbackMessage: 'Unable to load payments.' })
}

/**
 * Fetch a single payment by id.
 * @param {string} paymentId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventIds?: string[] }} [options]
 */
export const getPaymentById = async (paymentId, options = {}) => {
  const { role, userId, eventIds } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && (!eventIds || eventIds.length === 0)) {
    return accessDeniedResponse('Partners can only access payments for assigned events.')
  }

  let query = supabase.from(TABLE_NAME).select('*').eq('id', paymentId).maybeSingle()

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'agent_id',
    studentField: 'payer_id',
    eventIds
  })

  return handleQuery(query, {
    fallbackMessage: 'Unable to load the payment.',
    allowEmpty: false
  })
}

/**
 * Create a new payment record.
 * @param {Record<string, any>} payload
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const createPayment = async (payload, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && !payload?.event_id) {
    return accessDeniedResponse('Partners can only record payments for assigned events.')
  }

  if ((resolvedRole === 'agent' || resolvedRole === 'partner') && payload?.event_id) {
    const access = await ensureCanManageEvent(payload.event_id)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  const data = applyOwnershipToPayload(payload, {
    role: resolvedRole,
    userId: resolvedUserId,
    studentField: 'payer_id',
    agentField: 'agent_id'
  })

  const query = supabase.from(TABLE_NAME).insert(data).select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to record the payment.',
    allowEmpty: false
  })
}

/**
 * Update a payment record.
 * @param {string} paymentId
 * @param {Record<string, any>} updates
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const updatePayment = async (paymentId, updates, options = {}) => {
  const { role, userId, eventId, eventIds } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner') {
    const scopedEventId = eventId || updates?.event_id
    if (!scopedEventId && (!eventIds || eventIds.length === 0)) {
      return accessDeniedResponse('Partners can only update payments for assigned events.')
    }
  }

  const scopedEventId = eventId || updates?.event_id
  if ((resolvedRole === 'agent' || resolvedRole === 'partner') && scopedEventId) {
    const access = await ensureCanManageEvent(scopedEventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  let query = supabase.from(TABLE_NAME).update(updates).eq('id', paymentId)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'agent_id',
    studentField: 'payer_id',
    eventIds
  })

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to update the payment.',
    allowEmpty: false
  })
}

/**
 * Delete a payment record.
 * @param {string} paymentId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const deletePayment = async (paymentId, options = {}) => {
  const { role, userId, eventId, eventIds } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner') {
    if (!eventId && (!eventIds || eventIds.length === 0)) {
      return accessDeniedResponse('Partners can only delete payments for assigned events.')
    }
  }

  if ((resolvedRole === 'agent' || resolvedRole === 'partner') && eventId) {
    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  let query = supabase.from(TABLE_NAME).delete().eq('id', paymentId)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'agent_id',
    studentField: 'payer_id',
    eventIds
  })

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to delete the payment.',
    allowEmpty: false
  })
}

/**
 * Fetch payment summary metrics.
 * @param {{
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string,
 *   eventId?: string
 * }} options
 */
export const getPaymentSummary = async (options = {}) => {
  const { role, userId, eventId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const params = {
    event_id: eventId
  }

  if (resolvedRole === 'student') {
    params.payer_id = roleContext.userId
  }

  if (resolvedRole === 'agent') {
    params.agent_id = roleContext.userId
  }

  if (resolvedRole === 'partner' && eventId) {
    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  return callRpc('get_payment_summary', params, 'Unable to load payment summary.')
}
